import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Localised document titles.
 *
 * Route `title` values are translation keys (`title.dashboard`), resolved at
 * navigation time and again whenever the language changes, so the browser tab
 * follows the active locale. The product name is appended untranslated — the
 * brand is "Nabd / نبض" and the tab needs to be findable by either.
 */
@Injectable({ providedIn: 'root' })
export class TranslatedTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private lastKey: string | undefined;

  constructor() {
    super();

    // Re-resolve when a catalogue finishes loading and whenever the language
    // flips. `translate()` returns the raw key if the catalogue has not landed
    // yet, so resolving only at navigation time would leave `title.dashboard`
    // in the browser tab on a cold load.
    this.transloco.events$.subscribe((event) => {
      if (event.type === 'translationLoadSuccess' || event.type === 'langChanged') {
        this.apply();
      }
    });
  }

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.lastKey = this.buildTitle(snapshot);
    this.apply();
  }

  private apply(): void {
    if (!this.lastKey) {
      this.title.setTitle('Nabd');
      return;
    }

    const resolved = this.transloco.translate(this.lastKey);

    // Before the catalogue loads, `translate` echoes the key. Showing the bare
    // product name is better than showing `title.dashboard`.
    this.title.setTitle(resolved === this.lastKey ? 'Nabd' : `${resolved} · Nabd`);
  }
}
